/**
 * useAutoMeasureOnStop — hook + toggle for automatic measurement when
 * the TS is tracking a prism and the pole holder stops moving.
 *
 * The user's workflow: they've enabled tracking on the TS itself so
 * the head is following the prism. Rather than physically tap "Take
 * Shot" every time they reach a check point, we watch the polled
 * angles: if the TS is tracking AND the angles have stayed within a
 * small window for ~1 second, we fire one measurement + call the
 * caller-supplied `onFire` (which usually posts the point to the
 * session).
 *
 * Off by default. Toggled via the `<AutoMeasureToggle />` component.
 * Includes:
 *   - a small dead-band on Hz/V (5 mrad total motion)
 *   - a hold time (default 1000 ms) before it fires
 *   - a cooldown (default 3 s) so it doesn't fire twice on the same stop
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { station } from "../lib/api";

const HOLD_MS = 1000;
const COOLDOWN_MS = 3000;
const DEAD_BAND_RAD = 0.005;      // ~0.3° window for "stopped"

export function useAutoMeasureOnStop({ enabled, onFire }) {
  const winRef = useRef([]);       // recent {hz, v, t} samples
  const lastFireRef = useRef(0);
  const firingRef = useRef(false);

  useEffect(() => {
    if (!enabled) { winRef.current = []; return; }
    let cancelled = false;

    const tick = async () => {
      try {
        const s = await station.state();
        if (cancelled) return;
        const now = Date.now();
        // Only meaningful when TS is actively tracking a prism.
        if (!s?.ts_tracking) { winRef.current = []; return; }
        winRef.current.push({ hz: s.hz, v: s.v, t: now });
        // Drop anything older than HOLD_MS.
        winRef.current = winRef.current.filter((r) => now - r.t <= HOLD_MS);
        // Need enough samples spanning at least HOLD_MS to declare "stopped".
        if (winRef.current.length < 2) return;
        const span = winRef.current[winRef.current.length - 1].t - winRef.current[0].t;
        if (span < HOLD_MS) return;
        // Compute Hz/V ranges across the window.
        const hzs = winRef.current.map((r) => r.hz);
        const vs = winRef.current.map((r) => r.v);
        const hzRange = Math.max(...hzs) - Math.min(...hzs);
        const vRange = Math.max(...vs) - Math.min(...vs);
        if (hzRange > DEAD_BAND_RAD || vRange > DEAD_BAND_RAD) return;
        // Also respect cooldown between fires.
        if (now - lastFireRef.current < COOLDOWN_MS) return;
        if (firingRef.current) return;
        firingRef.current = true;
        lastFireRef.current = now;
        winRef.current = [];
        try {
          const m = await station.measure();
          if (Number.isFinite(m?.x)) {
            toast.success("Auto-shot (prism stopped)", { duration: 1500 });
            await onFire(m);
          }
        } catch { /* silent — we'll try again next stop */ }
        finally { firingRef.current = false; }
      } catch { /* silent */ }
    };

    const id = setInterval(tick, 250);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, onFire]);
}

/** Small toggle chip you can drop next to a Take Shot button. */
export function AutoMeasureToggle({ value, onChange, testid }) {
  return (
    <button
      data-testid={testid || "auto-measure-toggle"}
      onClick={() => onChange(!value)}
      className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-all ${
        value
          ? "border-[#00E1C7] bg-[#00E1C7]/15 text-[#00E1C7]"
          : "border-[#2A2E39] bg-[#14161A] text-[#8B939A] hover:border-[#00E1C7]/60"
      }`}
      style={{ fontFamily: "Chakra Petch, sans-serif" }}
      title="Auto-fire a measurement when the TS is tracking and the prism stops for ~1 s"
    >
      <span>Auto-shot on prism stop</span>
      <span className={`h-2 w-2 rounded-full ${value ? "bg-[#00E1C7] shadow-[0_0_6px_#00E1C7]" : "bg-[#4A5054]"}`} />
    </button>
  );
}

/** Convenience state hook — persists the toggle in localStorage per tool. */
export function useAutoMeasureState(toolKey) {
  const storageKey = `fabaxis:auto-measure:${toolKey}`;
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  });
  const setPersisted = useCallback((next) => {
    try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ok */ }
    setEnabled(next);
  }, [storageKey]);
  return [enabled, setPersisted];
}
